<template>
  <div
    v-show="!uploading && !uploaded"
    class="flex flex-col flex-1 justify-center"
  >
    <MainInstruction :isHybrid="isHybrid" @video-duration="setCooldownTime" />
    <div class="fixed bottom-8 inset-x-0 mt-4 flex justify-center">
      <div class="flex justify-center">
        <label
          :class="[
            disabledButton
              ? 'text-white bg-[#999] cursor-not-allowed'
              : 'text-[var(--text-button)] bg-[var(--primary)]'
          ]"
          class="p-2.5 text-lg rounded font-bold leading-[normal] capitalize transition-all duration-300 ease-in-out"
          for="file-upload"
          @click="handleButtonClick"
        >
          <transition mode="out-in" name="fade">
            <span :key="disabledButton" class="leading-[0]">
              {{ $t('instruction.popup.take-photo') }}
              <span v-if="disabledButton">({{ cooldownTime }})</span>
            </span>
          </transition>
        </label>
        <input
          id="file-upload"
          ref="fileInput"
          :disabled="disabledButton"
          accept="image/jpg, image/jpeg"
          capture="environment"
          class="hidden"
          type="file"
          @change="onFileChange"
        />
      </div>
    </div>
  </div>
  <PreloadScan v-if="uploading" :photo="previewSrc" />

  <ResponseResult
    v-if="uploaded"
    :decodedData="decodedData"
    :deviceInfo="deviceInfo"
    :imageQuality="imageQuality"
    :installId="installId"
    :isHybrid="isHybrid"
    :photo="previewSrc"
    :requestStatus="requestStatus"
    :status="authStatus"
  />
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as amplitude from '@amplitude/analytics-browser'
import { events } from '@/utils/events'
import authenticationService from '@/utils/authentication'
import { getLocation } from '@/utils/getLocation'
import MainInstruction from './MainInstruction.vue'
import PreloadScan from './PreloadScan.vue'
import ResponseResult from './ResponseResult.vue'
import { makeId } from '@/utils/uniqueId.js'
import DataLogger from '@/utils/dataLogger.js'
import { generateUUID } from '@/utils/uuid.js'
import { sendPhotoAuthToNative } from '@/utils/nativeBridge.js'
import { getStoredValue, setStoredValue } from '@/utils/storage.js'

const props = defineProps({
  queryScanId: String,
  decodedData: Object,
  isStc: Boolean,
  isHybrid: Boolean,
  deviceInfo: Object
})

const capturing = ref(false)
const uploading = ref(false)
const uploaded = ref(false)
const authStatus = ref('')
const requestStatus = ref(null)
const imageQuality = ref([])
const previewSrc = ref('#')
const fileInput = ref(null)
const cooldownTime = ref(3)
const disabledButton = ref(true)

const sessionId = makeId(16)
const installId = ref(null)
const scansSessionHint = ref(props.queryScanId)
const app_version = __APP_VERSION__

let dataLogger = new DataLogger(sessionId)

const handleVisibilityChange = () => {
  // Reset scansSessionHint
  if (document.visibilityState === 'hidden' && capturing.value === false) {
    scansSessionHint.value = null
  }

  if (document.visibilityState === 'visible') {
    capturing.value = false
  }
}

document.addEventListener('visibilitychange', handleVisibilityChange)
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})

onMounted(async () => {
  initInstallId()
  const identifyEvent = new amplitude.Identify()
  identifyEvent.set('Device model', props.deviceInfo?.model)
  identifyEvent.set('Browser', props.deviceInfo?.browser)
  identifyEvent.set('Browser Version', props.deviceInfo?.browserVersion)
  identifyEvent.set('Browser Engine', props.deviceInfo?.browserEngine)
  identifyEvent.set('App version', __APP_VERSION__)
  amplitude.identify(identifyEvent)

  events.value.locationData = await getLocation(props.decodedData)
  disabledButton.value = true

  const interval = setInterval(() => {
    cooldownTime.value--
    if (cooldownTime.value <= 0) {
      clearInterval(interval)
      disabledButton.value = false
    }
  }, 1000)
})

const initInstallId = () => {
  const storedId = getStoredValue('installId')
  installId.value = storedId || generateUUID()
  if (!storedId) {
    setStoredValue('installId', installId.value)
  }
}

const setCooldownTime = (videoDuration) => {
  if (!getStoredValue('seenInstruction')) {
    setStoredValue('seenInstruction', true)

    cooldownTime.value = videoDuration
  }
}

const handleButtonClick = () => {
  if (disabledButton.value) return

  capturing.value = true

  amplitude.track('Take a picture', { ...props.decodedData })
  dataLogger.recordTimeOpenCamera()
}

const onFileChange = (e) => {
  if (disabledButton.value) return

  let files = e.target.files || e.dataTransfer.files
  const picture = files[0]
  let reader = new FileReader()

  reader.onload = (e) => {
    previewSrc.value = e.target.result
  }

  reader.readAsDataURL(picture)
  amplitude.track('Confirm picture', { ...props.decodedData })

  uploadPicture(picture)
}

const restartAuthFlow = () => {
  amplitude.track('Retake photo', { ...props.decodedData })
  capturing.value = true
  dataLogger.incrementAttemptId()

  dataLogger.recordTimeOpenCamera()
  fileInput.value.value = ''
  fileInput.value.click()
  uploaded.value = false
  uploading.value = false
}

watch(
  () => events.value.triggerTakePicture,
  (newValue) => {
    if (newValue) {
      restartAuthFlow()
      events.value.triggerTakePicture = false
    }
  }
)

watch(
  () => events.value.goHome,
  (newValue) => {
    if (newValue) {
      uploaded.value = false
      uploading.value = false
      events.value.goHome = false
    }
  }
)

const uploadPicture = async (picture) => {
  try {
    if (uploading.value || !picture) return

    uploading.value = true
    dataLogger.authTimeStamp = Date.now()
    dataLogger.recordTimeAuthCandidate()

    const authResult = await authenticationService.upload(
      picture,
      installId.value,
      props.decodedData,
      scansSessionHint.value,
      props.deviceInfo
    )
    let scan = {}

    if (authResult) {
      dataLogger.recordTimeResponseReceived()
      console.log('Get auth result: ', authResult)
      events.value.carrier_content = authResult.data.carrier_content
      events.value.third_party_content_details =
        authResult.data.third_party_content_details
      authStatus.value = authResult.data.authentication_status
      imageQuality.value = authResult.data.image_quality
      scan = authResult.data.scan
      requestStatus.value = authResult.status

      sendPhotoAuthToNative(authStatus.value, {
        ...authResult.data,
        session_id: sessionId
      })

      if (authStatus.value) {
        if (!scansSessionHint.value) {
          scansSessionHint.value = scan.uuid?.toString()
        }
        dataLogger.logScanData(
          scan?.uuid,
          props.deviceInfo?.model,
          props.deviceInfo?.browser,
          props.deviceInfo?.browserVersion,
          props.deviceInfo?.browserEngine,
          window.location.href,
          app_version,
          scan?.result,
          scan?.auth_failure_mode,
          authStatus.value
        )
      }
    }
    //redirect
    const redirectStatuses = [
      'auth_original',
      'auth_copy',
      'sid',
      'inactive',
      'blacklisted',
      'not_trained'
    ]
    if (redirectStatuses.includes(authStatus.value) && scan.consumer_url) {
      if (!props.isStc) {
        window.addEventListener('pagehide', () => {
          uploading.value = false
        })
        window.location.href = scan.consumer_url
      } else {
        window.parent.postMessage(
          {
            type: 'authentication-complete',
            scan: scan.uuid,
            url: scan.consumer_url
          },
          '*'
        )
        uploading.value = false
      }
    } else {
      uploading.value = false
      uploaded.value = true
    }
    // end redirect

    let coreTechResult = JSON.parse(JSON.stringify(authResult.data))
    coreTechResult.version = app_version
    amplitude.track('Scan Result', coreTechResult)
  } catch (error) {
    dataLogger.recordTimeResponseReceived()

    uploading.value = false
    uploaded.value = true
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
